import React from 'react';
import { Box, Typography } from '@mui/material';
import { Lock as LockIcon } from '@mui/icons-material';
import { EDIT_OBJECT_TOKENS } from './editObjectTokens';

export interface PrivateOnlyBannerProps {
  /** Headline text. Defaults to a sensible private-only message. */
  title?: string;
  /** Supporting copy. Defaults to a sensible explainer. */
  description?: string;
}

/**
 * Pale-yellow warning band shown at the top of the Private Only step
 * (and any other always-private section). Mirrors the design's
 * `card-sub-note` block: lock icon on the left, headline + supporting
 * copy that the surrounded fields are never exposed publicly.
 */
export const PrivateOnlyBanner: React.FC<PrivateOnlyBannerProps> = ({
  title = 'These details are never shown publicly',
  description = 'Information you enter here stays in your private records — it is not included in the public catalog page.',
}) => (
  <Box
    data-testid="private-only-banner"
    sx={{
      display: 'flex',
      alignItems: 'flex-start',
      gap: 1.5,
      p: 2,
      mb: 2,
      borderRadius: `${EDIT_OBJECT_TOKENS.radius.md}px`,
      border: `1px solid #f0dcb6`,
      bgcolor: '#fffdf7',
    }}
  >
    <LockIcon
      aria-hidden
      sx={{ color: EDIT_OBJECT_TOKENS.color.warning, fontSize: 22, mt: '2px', flexShrink: 0 }}
    />
    <Box sx={{ minWidth: 0 }}>
      <Typography
        sx={{
          fontFamily: EDIT_OBJECT_TOKENS.font.sans,
          fontWeight: 600,
          fontSize: 13,
          color: EDIT_OBJECT_TOKENS.color.ink700,
          lineHeight: 1.3,
          mb: 0.5,
        }}
      >
        {title}
      </Typography>
      <Typography
        sx={{
          fontSize: 12,
          color: EDIT_OBJECT_TOKENS.color.slate500,
          lineHeight: 1.5,
        }}
      >
        {description}
      </Typography>
    </Box>
  </Box>
);
